import { Alert, IconButton } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import moment from 'moment';
import './ConversationHistory.scss';
import LocalTranslatedText from '@/translation/frontend/components/LocalTranslatedText';
import FilePreview from '../../file_uploads/frontend/FilePreview';
import IndeterminateLoadingBar from '../../frontend/elements/IndeterminateLoadingBar';
import { ConversationAvatar } from './ConversationAvatar';
import { useFrontendConfiguration } from '../../frontend/components/FrontendConfigurationProvider';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { titleCaseFieldName } from './ConversationUtilities';
import CircularProgress from '@mui/material/CircularProgress';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import EmailDraftReviewCard from './EmailDraftReviewCard';

let globalCurrentPlayingAudio = null;
let globalCurrentSetIsPlaying = null;

export function PlayAudioButton({ block, webChatConfiguration }) {
  const config = useFrontendConfiguration();
  const [isPlaying, setIsPlaying] = React.useState(false);

  const handleFinishedPlaying = useCallback(() => {
    setIsPlaying(false);
    globalCurrentPlayingAudio = null;
    globalCurrentSetIsPlaying = null;
  }, [setIsPlaying]);

  const handlePlayAudioClicked = useCallback(() => {
    const audioElement = document.getElementById(block.audio_id);
    if (audioElement) {
      if (globalCurrentPlayingAudio) {
        globalCurrentPlayingAudio.pause();
        globalCurrentSetIsPlaying(false);
      }
      if (audioElement !== globalCurrentPlayingAudio) {
        audioElement.load();
        audioElement.play();
        globalCurrentPlayingAudio = audioElement;
        globalCurrentSetIsPlaying = setIsPlaying;
        setIsPlaying(true);
      } else {
        globalCurrentPlayingAudio = null;
        globalCurrentSetIsPlaying = null;
        setIsPlaying(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <IconButton
      aria-label="Play Audio"
      onClick={handlePlayAudioClicked}
      size={'small'}
    >
      {isPlaying ? (
        <PauseCircleIcon
          sx={{
            fontSize: 16,
            fill: webChatConfiguration?.event_date_font_color,
          }}
        />
      ) : (
        <PlayCircleIcon
          sx={{
            fontSize: 16,
            fill: webChatConfiguration?.event_date_font_color,
          }}
        />
      )}
      <audio
        id={block?.audio_id}
        className={'event-audio'}
        controls={false}
        autoPlay={false}
        preload={'none'}
        controlsList={['nodownload', 'nofullscreen', 'noremoteplayback']}
        onEnded={handleFinishedPlaying}
        src={getContentBlockDisplayUrl(block, config)}
      />
    </IconButton>
  );
}

export function ConversationHistoryEvent({
  event,
  eventIndex,
  lastEvent,
  isLast,
  webChatConfiguration,
  conversation,
}) {
  const config = useFrontendConfiguration();
  const [isShowingInfoPopover, setIsShowingInfoPopover] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  // Normalize backend API URL to ensure trailing slash (consistent with getContentBlockDisplayUrl)
  const backendApiUrl = useMemo(() => {
    let url = config.frontend.backend_api_url;
    if (!url.endsWith('/')) {
      url += '/';
    }
    return url;
  }, [config.frontend.backend_api_url]);

  const generatedDocumentUrl = `${backendApiUrl}generated_document/${event?.metadata?.generated_document_id}/rendered`;

  // Detect if this is a "document-only" event - an action result that exists primarily
  // to display a generated document. For these events, we hide the message bubble since
  // the text content is meant for the LLM, not the end user.
  const isDocumentOnlyEvent = useMemo(() => {
    return (
      event.metadata?.generated_document_id &&
      event.module_id !== 'user_interactor'
    );
  }, [event.metadata?.generated_document_id, event.module_id]);

  const handleGeneratedDocumentClick = React.useCallback(
    (e) => {
      // Open the file in a new screen
      window.open(generatedDocumentUrl, '_blank');
      e.preventDefault();
    },
    [generatedDocumentUrl]
  );

  const handleAttachmentClick = React.useCallback(
    (block, e) => {
      // Open the file in a new screen
      window.open(getContentBlockDownloadUrl(block, config), '_blank');
      e.preventDefault();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generatedDocumentUrl]
  );

  const isDateDifferentThenLast = useMemo(() => {
    if (lastEvent) {
      return (
        moment(event.created_at).format('MMM Do') !==
        moment(lastEvent.created_at).format('MMM Do')
      );
    }
    return true;
  }, [event.created_at, lastEvent]);

  const handleClickShowInfoPopover = useCallback((event) => {
    setIsShowingInfoPopover(true);
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setIsShowingInfoPopover(false);
    setAnchorEl(null);
  }, []);

  let isDateDifferentThenLastClass = isDateDifferentThenLast
    ? 'date-different'
    : '';

  const dateHeader = useMemo(() => {
    if (!isDateDifferentThenLast) return null;

    const eventDate = moment(event.created_at);
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');

    if (eventDate.isSame(today, 'day')) {
      return <LocalTranslatedText text="Today" />;
    } else if (eventDate.isSame(yesterday, 'day')) {
      return <LocalTranslatedText text="Yesterday" />;
    } else if (eventDate.isAfter(moment().subtract(7, 'days'))) {
      // For day names, we'll use moment's localized day name
      return <LocalTranslatedText text={eventDate.format('dddd')} />;
    }
    return <LocalTranslatedText text={eventDate.format('MMM DD')} />;
  }, [event.created_at, isDateDifferentThenLast]);

  const eventBackgroundColor =
    event.role === 'user'
      ? (webChatConfiguration?.user_message_background_color ??
        'rgb(2, 136, 209)')
      : event.role === 'tool'
        ? (webChatConfiguration?.action_result_background_color ??
          'rgb(46, 125, 50)')
        : event.action_id === 'send_message'
          ? (webChatConfiguration?.assistant_message_background_color ??
            'rgb(233, 236, 241)')
          : (webChatConfiguration?.action_call_background_color ??
            'rgb(206, 108, 6)');

  const eventFontColor =
    event.role === 'user'
      ? (webChatConfiguration?.user_message_font_color ?? 'rgb(255, 255, 255)')
      : event.role === 'tool'
        ? (webChatConfiguration?.action_result_font_color ??
          'rgb(255, 255, 255)')
        : event.action_id === 'send_message'
          ? (webChatConfiguration?.assistant_message_font_color ??
            'rgb(68, 68, 68)')
          : (webChatConfiguration?.action_call_font_color ??
            'rgb(255, 255, 255)');

  const audioBlocks = (event.content_blocks ?? []).filter(
    (block) => block.type === 'audio' && block.audio_id
  );
  const nonAudioBlocks = (event.content_blocks ?? []).filter(
    (block) => block.type !== 'audio'
  );

  return (
    <>
      {isDateDifferentThenLast && (
        <div
          className="date-separator"
          style={{ borderColor: webChatConfiguration?.event_date_font_color }}
        >
          <div
            className="date-header"
            style={{
              backgroundColor: webChatConfiguration?.widget_background_color,
              color: webChatConfiguration?.event_date_font_color,
            }}
          >
            {dateHeader}
          </div>
        </div>
      )}
      <div
        className={`event ${event.role} ${event.action_id} ${event.module_id} ${isDateDifferentThenLastClass}`}
      >
        {event.metadata?.generated_document_id ? (
          <div className={'event-file'}>
            <a
              href={generatedDocumentUrl}
              target={'_blank'}
              rel="noreferrer"
              onClick={handleGeneratedDocumentClick}
            >
              <FilePreview
                fileId={event.metadata?.file_id}
                fileName={`${event.metadata?.generated_document_title}.pdf`}
              />
            </a>
          </div>
        ) : null}
        {/* Hide message bubble for document-only events (action results with documents) */}
        {/* The text content of these events is internal/LLM-facing, not user-facing */}
        {!isDocumentOnlyEvent && (
          <div className={`event-inner ${event.role}`}>
            {event.role === 'assistant' || event.role === 'tool' ? (
              <div className={'event-role-bot'}>
                <ConversationAvatar
                  avatarUrl={webChatConfiguration?.avatar_url}
                  role={event.role}
                  eventType={event.event_type}
                  moduleId={event.module_id}
                  circleColor={webChatConfiguration?.role_icon_color}
                  iconColor={webChatConfiguration?.widget_background_color}
                />
              </div>
            ) : null}
            <div className={'header-alert-combo-widget'}>
              <div
                className={`event-header ${event.role}`}
                style={{
                  color:
                    webChatConfiguration?.chat_header_font_color ||
                    webChatConfiguration?.event_date_font_color,
                  ...(webChatConfiguration?.chat_header_background_color
                    ? {
                        backgroundColor:
                          webChatConfiguration.chat_header_background_color,
                        padding: '4px 8px',
                        borderRadius: '4px',
                      }
                    : {}),
                }}
              >
                <span
                  className={'role-name'}
                  style={{
                    color:
                      webChatConfiguration?.chat_header_font_color ||
                      webChatConfiguration?.role_name_font_color,
                  }}
                >
                  {event.role !== 'user' ? (
                    (event.event_type === 'action' &&
                      event.module_id !== 'user_interactor') ||
                    event.event_type === 'async_action_call' ||
                    event.event_type === 'async_action_result' ? (
                      <>
                        {event.event_type === 'action' ? (
                          <LocalTranslatedText text="Action" />
                        ) : null}
                        {event.event_type === 'async_action_call' ? (
                          <LocalTranslatedText text="Action Call" />
                        ) : null}
                        {event.event_type === 'async_action_result' ? (
                          <LocalTranslatedText text="Action Result" />
                        ) : null}
                      </>
                    ) : (
                      <LocalTranslatedText
                        text={webChatConfiguration?.agent_title || 'Agent'}
                      />
                    )
                  ) : null}
                </span>
                {event.role === 'user' ? (
                  <>
                    <IconButton
                      aria-label="Detailed Info"
                      onClick={handleClickShowInfoPopover}
                      size={'small'}
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          fill: webChatConfiguration?.event_date_font_color,
                        }}
                      />
                    </IconButton>
                    {audioBlocks.map((block, blockIndex) => {
                      return (
                        <PlayAudioButton
                          key={blockIndex}
                          webChatConfiguration={webChatConfiguration}
                          block={block}
                        />
                      );
                    })}
                  </>
                ) : null}
                {!event.created_at ? (
                  <LocalTranslatedText text="Invalid Date" />
                ) : (
                  moment
                    .utc(event.created_at)
                    .local()
                    .format(
                      isDateDifferentThenLast ? 'MMM Do, h:mm a' : 'h:mm a'
                    )
                )}
                {event.role === 'assistant' || event.role === 'tool' ? (
                  <>
                    <IconButton
                      aria-label="Detailed Info"
                      onClick={handleClickShowInfoPopover}
                      size={'small'}
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          fill: webChatConfiguration?.event_date_font_color,
                        }}
                      />
                    </IconButton>
                    {audioBlocks.map((block, blockIndex) => {
                      return (
                        <PlayAudioButton
                          key={blockIndex}
                          webChatConfiguration={webChatConfiguration}
                          block={block}
                        />
                      );
                    })}
                  </>
                ) : null}

                {event.role === 'user' ? (
                  <span
                    className={'role-name'}
                    style={{
                      color:
                        webChatConfiguration?.chat_header_font_color ||
                        webChatConfiguration?.role_name_font_color,
                    }}
                  >
                    <LocalTranslatedText text={event.name || 'User'} />
                  </span>
                ) : null}
              </div>

              {event.metadata?.email_draft_review ? (
                <EmailDraftReviewCard
                  review={event.metadata.email_draft_review}
                  conversationId={conversation?._id ?? conversation?.id}
                  conversationToken={
                    conversation?.metadata?.conversation_token
                  }
                  webChatConfiguration={webChatConfiguration}
                />
              ) : (
                <Alert
                  variant="filled"
                  severity={event.role === 'user' ? 'info' : 'success'}
                  icon={null}
                  style={{
                    backgroundColor: eventBackgroundColor,
                  }}
                >
                  <div
                    className={'event-text'}
                    style={{
                      color: eventFontColor,
                    }}
                  >
                    {event?.event_type === 'async_action_call' &&
                    event.async_action_status !== 'pending' ? (
                      <LocalTranslatedText
                        text={`Action ${titleCaseFieldName(event.action_id)} has completed.`}
                        showOptionToViewOriginalLanguage={true}
                      />
                    ) : (
                      <LocalTranslatedText
                        text={String(event.content).trim()}
                        showOptionToViewOriginalLanguage={true}
                      />
                    )}
                    {event?.async_action_status === 'pending' ? (
                      <div className={'pending-action'}>
                        <CircularProgress
                          sx={{
                            color: eventFontColor,
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </Alert>
              )}
            </div>
            {event.role === 'user' ? (
              <div className={'event-role-user'}>
                <ConversationAvatar
                  avatarUrl={webChatConfiguration?.avatar_url}
                  role={event.role}
                  eventType={event.event_type}
                  moduleId={event.module_id}
                  circleColor={webChatConfiguration?.role_icon_color}
                  iconColor={webChatConfiguration?.widget_background_color}
                />
              </div>
            ) : null}
          </div>
        )}
        {nonAudioBlocks.map((block, blockIndex) => {
          return (
            <div key={blockIndex} className={'event-content-block'}>
              {block?.type === 'text' ? (
                <LocalTranslatedText
                  text={block?.text}
                  showOptionToViewOriginalLanguage={true}
                />
              ) : null}

              {block?.type === 'image' ? (
                <a
                  className={'event-image'}
                  target="_blank"
                  href={getContentBlockDownloadUrl(block, config)}
                  onClick={handleAttachmentClick.bind(null, block)}
                  rel="noreferrer"
                >
                  <img
                    className={'image-attachment'}
                    src={getContentBlockDisplayUrl(block, config)}
                    href={getContentBlockDownloadUrl(block, config)}
                    alt={block?.image_id}
                  />
                </a>
              ) : null}

              {block?.type === 'file' ? (
                <a
                  href={getContentBlockDownloadUrl(block, config)}
                  target="_blank"
                  className={'event-image'}
                  onClick={handleAttachmentClick.bind(null, block)}
                  rel="noreferrer"
                >
                  <FilePreview
                    fileId={block?.file_id}
                    fileName={`document.pdf`}
                  />
                </a>
              ) : null}
            </div>
          );
        })}
        {event.metadata?.show_loading_bar && isLast ? (
          <div className={'event-loading-bar'}>
            <IndeterminateLoadingBar
              halfLifeSeconds={event.metadata?.loading_bar_halflife}
            />
          </div>
        ) : null}
        <Popover
          open={isShowingInfoPopover}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Typography sx={{ p: 1 }}>Event ID: {event?.id}</Typography>
          {event.event_index !== null ? (
            <Typography sx={{ p: 1 }}>
              Event Index: {event?.event_index}
            </Typography>
          ) : null}
          {event.event_type !== null ? (
            <Typography sx={{ p: 1 }}>
              Event Type: {event?.event_type}
            </Typography>
          ) : null}
          {event.role ? (
            <Typography sx={{ p: 1 }}>Role: {event?.role}</Typography>
          ) : null}
          <Typography sx={{ p: 1 }}>
            Date:{' '}
            {moment.utc(event.created_at).local().format('MMM Do, h:mm:ss a')}
          </Typography>
          {event.module_id ? (
            <Typography sx={{ p: 1 }}>Module ID: {event?.module_id}</Typography>
          ) : null}
          {event.action_id ? (
            <Typography sx={{ p: 1 }}>Action ID: {event?.action_id}</Typography>
          ) : null}
          {event.tool_name ? (
            <Typography sx={{ p: 1 }}>Tool Name: {event?.tool_name}</Typography>
          ) : null}
          {event.name ? (
            <Typography sx={{ p: 1 }}>Name: {event?.name}</Typography>
          ) : null}
          {event?.action_parameters &&
          Object.keys(event?.action_parameters).length > 0
            ? Object.keys(event?.action_parameters).map((key) => (
                <Typography sx={{ p: 1, whiteSpace: 'pre' }} key={key}>
                  {titleCaseFieldName(key)}:&nbsp;
                  {JSON.stringify(event?.action_parameters[key], null, 2)
                    .replace(/^"/, '')
                    .replace(/"$/, '')}
                </Typography>
              ))
            : null}
          {event?.metadata && Object.keys(event?.metadata).length > 0
            ? Object.keys(event?.metadata).map((key) => (
                <Typography sx={{ p: 1, whiteSpace: 'pre' }} key={key}>
                  {titleCaseFieldName(key)}:&nbsp;
                  {JSON.stringify(event?.metadata[key], null, 2)
                    .replace(/^"/, '')
                    .replace(/"$/, '')}
                </Typography>
              ))
            : null}
        </Popover>
      </div>
    </>
  );
}

export function ConversationHistory({
  events,
  onlyShowMessages,
  conversation,
  webChatConfiguration,
}) {
  let filteredEvents = events;
  if (onlyShowMessages) {
    filteredEvents = filteredEvents.filter((event) => {
      // Keep user messages and agent messages to user
      if (event.module_id === 'user_interactor' || event.role === 'user') {
        return true;
      }
      // Also keep events with generated documents (for document preview visibility in plugin)
      // This allows users to see and download documents they paid for, even though the
      // underlying event is an action result (which would normally be filtered out)
      if (event.metadata?.generated_document_id) {
        return true;
      }
      return false;
    });
  }

  return (
    <div
      className={
        'prospera-labs-agent-platform-chat-widget-conversation-history'
      }
    >
      {filteredEvents.map((event, eventIndex) => (
        <ConversationHistoryEvent
          key={event._id ?? event.id ?? eventIndex}
          event={event}
          eventIndex={eventIndex}
          lastEvent={eventIndex > 0 ? filteredEvents[eventIndex - 1] : null}
          isLast={eventIndex === filteredEvents.length - 1}
          webChatConfiguration={webChatConfiguration}
          conversation={conversation}
        />
      ))}
    </div>
  );
}

function getContentBlockDisplayUrl(contentBlock, config) {
  let backendApiUrl = config.frontend.backend_api_url;
  if (!backendApiUrl.endsWith('/')) {
    backendApiUrl += '/';
  }

  if (contentBlock.type === 'text') {
    return contentBlock.text;
  } else if (contentBlock.type === 'image') {
    return `${backendApiUrl}image/${contentBlock.image_id}/contents`;
  } else if (contentBlock.type === 'audio') {
    return `${backendApiUrl}audio_upload/${contentBlock.audio_id}/contents`;
  } else if (contentBlock.type === 'file') {
    return `${backendApiUrl}file/${contentBlock.file_id}/preview`;
  }
  return null;
}

function getContentBlockDownloadUrl(contentBlock, config) {
  let backendApiUrl = config.frontend.backend_api_url;
  if (!backendApiUrl.endsWith('/')) {
    backendApiUrl += '/';
  }

  if (contentBlock.type === 'text') {
    return contentBlock.text;
  } else if (contentBlock.type === 'image') {
    return `${backendApiUrl}image/${contentBlock.image_id}/contents`;
  } else if (contentBlock.type === 'audio') {
    return `${backendApiUrl}audio_upload/${contentBlock.audio_id}/contents`;
  } else if (contentBlock.type === 'file') {
    return `${backendApiUrl}file/${contentBlock.file_id}/contents`;
  }
  return null;
}
